import { IconTile } from "../../../../../../components/icon/IconTile.tsx";
import type { VoidComponent } from "solid-js";
import { createMemo, Show } from "solid-js";
import PlanStatusPill from "./PlanStatusPill.tsx";
import { calculateRemainingDays } from "../../../../../../utils/planUtil.ts";
import { useTranslation } from "../../../../../../state/i18n.tsx";
import { formatDate } from "../../../../../../components/formatting/FormatDate.tsx";
import ContactUsAction from "./actions/ContactUsAction.tsx";
import ChoosePlanAction from "./actions/ChoosePlanAction.tsx";
import UpgradePlanAction from "./actions/UpgradePlanAction.tsx";
import { PlanInterval } from "../../../../../../services/BillingService.ts";

export type PlanStatus = "none" | "trial" | "active" | "pastDue" | "unpaid" | "blocked";

export type PlanDetails = {
  planName: string;
  interval: PlanInterval;
};

export type PlanProps =
  | { status: "none" }
  | (PlanDetails & { status: "active"; cycleEnd: number })
  | (PlanDetails & { status: "trial"; cycleEnd: number })
  | (PlanDetails & { status: "blocked" })
  | (PlanDetails & { status: "pastDue" })
  | (PlanDetails & { status: "unpaid" });

const Plan: VoidComponent<PlanProps> = (props) => {
  const { t } = useTranslation();

  const title = createMemo(() =>
    props.status === "none"
      ? t(($) => $.SETTINGS_BILLING_CURRENT_PLAN_PLAN_NO_ACTIVE_PLAN_TITLE)
      : `${props.planName} (${t(($) => (props.interval === "month" ? $.SETTINGS_BILLING_CURRENT_PLAN_INTERVAL_MONTH : $.SETTINGS_BILLING_CURRENT_PLAN_INTERVAL_YEAR))})`,
  );

  const subtitle = createMemo(() => {
    switch (props.status) {
      case "none":
        return t(($) => $.SETTINGS_BILLING_CURRENT_PLAN_PLAN_NO_ACTIVE_PLAN_SUBTITLE);
      case "active":
        return t(($) => $.SETTINGS_BILLING_CURRENT_PLAN_ACTIVE_SUBTITLE, {
          nextInvoiceDate: formatDate(new Date(props.cycleEnd * 1000), true),
        });
      case "trial": {
        const daysLeft = calculateRemainingDays(props.cycleEnd);
        return t(($) => $.SETTINGS_BILLING_CURRENT_PLAN_TRIAL_SUBTITLE, { count: daysLeft });
      }
      case "blocked":
        return t(($) => $.SETTINGS_BILLING_CURRENT_PLAN_BLOCKED_SUBTITLE);
      case "pastDue":
        return t(($) => $.SETTINGS_BILLING_CURRENT_PLAN_PAST_DUE_SUBTITLE);
      case "unpaid":
        return t(($) => $.SETTINGS_BILLING_CURRENT_PLAN_UNPAID_SUBTITLE);
    }
  });

  const actions = createMemo(() => {
    switch (props.status) {
      case "none":
        return [<ContactUsAction />, <ChoosePlanAction />];
      case "active":
      case "pastDue":
        return [<ContactUsAction />, <UpgradePlanAction />];
      case "blocked":
      case "unpaid":
        return [<ContactUsAction />];
      case "trial":
        return undefined;
    }
  });

  return (
    <div class="flex grow gap-4">
      <IconTile icon="cube" size="md" border />
      <div class="flex flex-col">
        <span class="flex items-center gap-2 text-medium-semibold text-primary">
          {title()}
          <Show when={props.status !== "none"}>
            <PlanStatusPill status={props.status} />
          </Show>
        </span>
        <span class="text-base-regular text-secondary">{subtitle()}</span>
      </div>
      <Show when={actions()}>{(actions) => <div class="ml-auto flex gap-2">{actions()}</div>}</Show>
    </div>
  );
};

export default Plan;
